import React, { useEffect, useState } from "react";
import { MonitorPlay, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { demoLink } from "@/lib/demo";
import { Card, Quiet } from "@/components/hub/Shell";
import { toast } from "sonner";

const SITE = (process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "");

/** The first demo link ever handed out — switched off by "New links", and back on from here. */
function OriginalLink({ original, onCopy, onRestore, busy }) {
  const url = original.key ? demoLink(SITE, original.key) : "";
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid="hub-demo-original">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-slate-900">
          Original link <span className={original.on ? "text-emerald-700" : "text-slate-400"} data-testid="hub-demo-original-state">
            · {original.on ? "working" : "switched off"}</span>
        </div>
        <div className="truncate font-mono text-[11.5px] text-slate-500" data-testid="hub-demo-original-url">
          {original.on ? url : "Links sent before you made new ones stop here. Turn it back on and they work again."}
        </div>
      </div>
      {original.on ? (
        <button onClick={() => onCopy(url)} title="Copy link" data-testid="hub-demo-original-copy"
                className="grid size-8 place-items-center rounded-[10px] border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900">
          <Copy className="size-3.5" />
        </button>
      ) : (
        <button onClick={onRestore} disabled={busy} data-testid="hub-demo-original-restore"
                className="inline-flex h-8 items-center rounded-[10px] bg-[#0055FF] px-3 text-[12px] font-semibold text-white transition-colors hover:bg-[#0044CC] disabled:opacity-60">
          Turn back on
        </button>
      )}
    </div>
  );
}

/** Demo portals — private links that open each partner portal on made-up data, for onboarding calls. */
export function DemoLinksCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const origin = window.location.origin;
  const copyAll = () => copy([
    "Connexis — live demo portals (private links, made-up data, nothing contacts anyone)", "",
    ...data.portals.map((p) => `${p.label}: ${demoLink(SITE, data.code, p.key)}`), "",
    `All five, to choose from: ${demoLink(SITE, data.code)}`,
  ].join("\n"));

  useEffect(() => { api.get("/hub/demo-links").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — select the link and copy it by hand"); }
  };
  const rotate = async () => {
    if (!window.confirm("Make new demo links? Every link you've already sent will stop working.")) return;
    setBusy(true);
    try { setData((await api.post("/hub/demo-links/rotate")).data); toast.success("New links made — the old ones no longer work"); }
    catch { toast.error("That didn't go through"); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try { setData((await api.post("/hub/demo-links/original")).data); toast.success("The original demo link works again"); }
    catch { toast.error("That didn't go through"); }
    finally { setBusy(false); }
  };

  if (data === false) return null;
  return (
    <Card label="Demo portals" Icon={MonitorPlay} testId="hub-demo-links"
          right={data && (
            <div className="flex items-center gap-1.5">
              <button onClick={copyAll} data-testid="hub-demo-copy-all-five"
                      className="inline-flex h-7 items-center gap-1.5 rounded-[10px] bg-[#0055FF] px-2.5 text-[11.5px] font-semibold text-white transition-colors hover:bg-[#0044CC]">
                <Copy className="size-3" /> Copy all 5
              </button>
              <button onClick={rotate} disabled={busy} data-testid="hub-demo-links-rotate"
                      className="inline-flex h-7 items-center gap-1.5 rounded-[10px] border border-slate-200 bg-white px-2.5 text-[11.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900">
                <RefreshCw className="size-3" /> New links
              </button>
            </div>
          )}>
      <Quiet>
        Send these on onboarding calls. Each one opens that portal straight away on made-up data — no sign-in, nothing it does
        contacts anyone, and the viewer can put everything back with Reset.
      </Quiet>
      {data && (
        <div className="mt-4 divide-y divide-slate-100">
          {data.portals.map((p) => {
            const url = demoLink(SITE, data.code, p.key);
            return (
              <div key={p.key} className="flex items-center gap-3 py-2.5" data-testid={`hub-demo-link-${p.key}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-900">{p.label}</div>
                  <div className="truncate font-mono text-[11.5px] text-slate-500" data-testid={`hub-demo-url-${p.key}`}>{url}</div>
                </div>
                <button onClick={() => copy(url)} title="Copy link" data-testid={`hub-demo-copy-${p.key}`}
                        className="grid size-8 place-items-center rounded-[10px] border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900">
                  <Copy className="size-3.5" />
                </button>
                <a href={demoLink(origin, data.code, p.key)} target="_blank" rel="noreferrer" title="Open in a new tab"
                   data-testid={`hub-demo-open-${p.key}`}
                   className="grid size-8 place-items-center rounded-[10px] border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900">
                  <ExternalLink className="size-3.5" />
                </a>
              </div>
            );
          })}
          <div className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-slate-900">All five, to choose from</div>
              <div className="truncate font-mono text-[11.5px] text-slate-500" data-testid="hub-demo-url-all">{demoLink(SITE, data.code)}</div>
            </div>
            <button onClick={() => copy(demoLink(SITE, data.code))} title="Copy link" data-testid="hub-demo-copy-all"
                    className="grid size-8 place-items-center rounded-[10px] border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900">
              <Copy className="size-3.5" />
            </button>
          </div>
          {data.original?.available && <OriginalLink original={data.original} onCopy={copy} onRestore={restore} busy={busy} />}
        </div>
      )}
    </Card>
  );
}

export default DemoLinksCard;
